import type { VisualShape } from 'types'
import type { ShapeContext, ShapeTarget } from './types'
import { resolveShape } from './shape-registry'
import { seededRandom } from './config'
import {
  DEFAULT_MORPH_DURATION_MS,
  MORPH_EASE_OUT,
  MORPH_EASE_OUT_DISSOLVE,
  MORPH_EASE_OUT_SPIRAL,
  resolveMorphTransition,
  type MorphStyle,
} from './shape-design-spec'

export type { MorphStyle } from './shape-design-spec'

/**
 * Interpolates between two visual shapes using cubic-bezier easing.
 * Supports per-style bezier curves and emits a shockwave on completion.
 */
export class ShapeMorpher {
  private from: VisualShape | null = null
  private to: VisualShape | null = null
  private startMs = 0
  private durationMs = DEFAULT_MORPH_DURATION_MS
  private _progress = 0
  private active = false
  private style: MorphStyle = 'flow'
  private shockMs = 0
  private shockDur = 300
  private onComplete: (() => void) | undefined = undefined
  private bezierControl = { x1: 0.42, y1: 0, x2: 0.58, y2: 1 }
  private shockwaveListeners: Array<() => void> = []

  /**
   * Begin a morph transition between two shapes.
   * @param from — Source shape name
   * @param to — Target shape name
   * @param durationMs — Transition duration in milliseconds
   * @param onComplete — Optional callback invoked when morph finishes
   */
  startMorph(
    from: VisualShape,
    to: VisualShape,
    durationMs?: number,
    onComplete?: () => void,
    style?: MorphStyle
  ): void {
    const transition = resolveMorphTransition(from, to)
    this.from = from
    this.to = to
    this.startMs = performance.now()
    this.durationMs = durationMs ?? transition.durationMs
    this._progress = 0
    this.active = true
    this.shockMs = this.startMs
    this.shockDur = Math.min(this.durationMs * 0.3, 400)
    this.onComplete = onComplete
    this.style = style ?? transition.defaultStyle
    this.applyStyle()
  }

  /** Shorthand for `startMorph` without completion callback. */
  morph(from: VisualShape, to: VisualShape, dur?: number): void {
    this.startMorph(from, to, dur)
  }

  /** Start a morph with a specific interpolation style. */
  morphWithStyle(from: VisualShape, to: VisualShape, style: MorphStyle): void {
    this.startMorph(from, to, undefined, undefined, style)
  }

  /** `true` while a morph transition is in progress. */
  isActive(): boolean { return this.active }
  /** Current morph progress in [0, 1]. */
  progress(): number { return this._progress }
  /** Alias for `progress()`. */
  getProgress(): number { return this._progress }
  /** Current target shape while a morph is active. */
  getTarget(): VisualShape | null { return this.to }

  /**
   * Advance the morph animation.
   * @param now — Current timestamp from `performance.now()`
   * @returns Current progress [0, 1]
   */
  update(now: number): number {
    if (!this.active) return this._progress
    const raw = Math.max(0, Math.min(1, (now - this.startMs) / this.durationMs))
    this._progress = this.bezierEase(raw)
    if (raw >= 1) {
      this.active = false
      this.from = null
      this.shockMs = now
      this.onComplete?.()
      this.emitShockwave()
    }
    return this._progress
  }

  /**
   * Resolve the interpolated shape target for a particle at the current morph progress.
   * @param ctx — Per-particle shape context
   * @returns Interpolated `ShapeTarget` between from and to shapes
   */
  resolve(ctx: ShapeContext): ShapeTarget {
    if (!this.active || !this.from || !this.to) {
      return resolveShape(this.to ?? this.from ?? 'orb', ctx)
    }
    const a = resolveShape(this.from, ctx)
    const b = resolveShape(this.to, ctx)
    const p = this._progress
    const now = performance.now()
    const sp = this.computeShock(ctx, now)
    const s = p
    const dx = b.x - a.x
    const dy = b.y - a.y
    const dist = Math.sqrt(dx * dx + dy * dy) || 1
    const bendStrength =
      this.style === 'spiral' ? 0.18 :
      this.style === 'dissolve' ? 0.028 : 0.07
    const bend = Math.min(72, dist * bendStrength) * Math.sin(Math.PI * s)
    const direction = seededRandom(ctx.index * 409 + 13) > 0.5 ? 1 : -1
    const arcX = (-dy / dist) * bend * direction
    const arcY = (dx / dist) * bend * direction
    const midAlpha =
      this.style === 'dissolve'
        ? 0.62 + 0.38 * Math.abs(0.5 - s) * 2
        : 0.82 + 0.18 * Math.sin(Math.PI * s)
    return {
      x: a.x + dx * s + arcX + sp.dx,
      y: a.y + dy * s + arcY + sp.dy,
      spring: a.spring + (b.spring - a.spring) * s,
      friction: a.friction + (b.friction - a.friction) * s,
      noise: a.noise + (b.noise - a.noise) * s,
      alphaHint: ((a.alphaHint ?? 1) + ((b.alphaHint ?? 1) - (a.alphaHint ?? 1)) * s) * midAlpha,
      depthScale: (a.depthScale ?? 1) + ((b.depthScale ?? 1) - (a.depthScale ?? 1)) * s,
      hueHint: (a.hueHint ?? 0) + ((b.hueHint ?? 0) - (a.hueHint ?? 0)) * s,
      bloomHint: (a.bloomHint ?? 0) + ((b.bloomHint ?? 0) - (a.bloomHint ?? 0)) * s,
    }
  }

  /**
   * Subscribe to the shockwave event emitted when morph completes.
   * @param cb — Callback invoked on shockwave
   * @returns Unsubscribe function
   */
  onShockwave(cb: () => void): () => void {
    this.shockwaveListeners.push(cb)
    return () => {
      const i = this.shockwaveListeners.indexOf(cb)
      if (i > -1) this.shockwaveListeners.splice(i, 1)
    }
  }

  private emitShockwave(): void {
    for (const cb of this.shockwaveListeners) cb()
  }

  private computeShock(ctx: ShapeContext, now: number): { dx: number; dy: number } {
    const dt = now - this.shockMs
    if (dt < 0 || dt > this.shockDur) return { dx: 0, dy: 0 }
    const t = dt / this.shockDur
    const amp = Math.sin(t * Math.PI) * 18 * (1 - t)
    const a = seededRandom(ctx.index * 977 + 31) * Math.PI * 2
    return { dx: Math.cos(a) * amp, dy: Math.sin(a) * amp }
  }

  private applyStyle(): void {
    switch (this.style) {
      case 'dissolve':
        this.bezierControl = { ...MORPH_EASE_OUT_DISSOLVE }
        break
      case 'spiral':
        this.bezierControl = { ...MORPH_EASE_OUT_SPIRAL }
        break
      default:
        this.bezierControl = { ...MORPH_EASE_OUT }
        break
    }
  }

  private bezierEase(t: number): number {
    const { x1, y1, x2, y2 } = this.bezierControl
    return this.cubicBezier(t, x1, y1, x2, y2)
  }

  private cubicBezier(t: number, x1: number, y1: number, x2: number, y2: number): number {
    let u = t
    for (let i = 0; i < 4; i++) {
      const x = 3 * u * (1 - u) * (1 - u) * x1 + 3 * u * u * (1 - u) * x2 + u * u * u
      const dx = 3 * (1 - u) * (1 - u) * x1 + 6 * u * (1 - u) * (x2 - x1) + 3 * u * u * (1 - x2)
      if (Math.abs(dx) < 1e-6) break
      u -= (x - t) / dx
      u = Math.max(0, Math.min(1, u))
    }
    return 3 * u * (1 - u) * (1 - u) * y1 + 3 * u * u * (1 - u) * y2 + u * u * u
  }
}

// Singleton instance for backward compatibility with old import sites
export const shapeMorpher = new ShapeMorpher()
